import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type StatusType = 
  | "active" | "expired" | "needs_login" | "waiting_otp" | "suspended"
  | "pending" | "visited" | "connected" | "messaged" | "replied" | "failed"
  | "executing" | "completed" | "cancelled" | "paused" | "success"
  | "disconnected" | "syncing" | "reconnect_needed" | "error" | "draft"
  | "starting" | "resuming" | "archived" | "churned";

interface StatusBadgeProps {
  status: StatusType;
  className?: string;
}

const statusConfig: Record<StatusType, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
  active: { label: "Connected", variant: "default", className: "bg-green-500/10 text-green-600 border-green-500/20" },
  expired: { label: "Expired", variant: "destructive" },
  needs_login: { label: "Needs Login", variant: "outline", className: "border-warning text-warning" },
  waiting_otp: { label: "Waiting OTP", variant: "outline", className: "border-accent text-accent" },
  suspended: { label: "Suspended", variant: "destructive" },
  pending: { label: "Pending", variant: "secondary" },
  visited: { label: "Visited", variant: "outline", className: "border-muted-foreground text-muted-foreground" },
  connected: { label: "Connected", variant: "default", className: "bg-linkedin hover:bg-linkedin/90" },
  messaged: { label: "Messaged", variant: "default", className: "bg-primary hover:bg-primary/90" },
  replied: { label: "Replied", variant: "default", className: "bg-success hover:bg-success/90" },
  failed: { label: "Failed", variant: "destructive" },
  executing: { label: "Executing", variant: "outline", className: "border-accent text-accent animate-pulse" },
  completed: { label: "Completed", variant: "default", className: "bg-success hover:bg-success/90" },
  cancelled: { label: "Cancelled", variant: "secondary" },
  paused: { label: "Paused", variant: "secondary" },
  starting: { label: "Starting...", variant: "outline", className: "border-blue-500 text-blue-600 animate-pulse" },
  resuming: { label: "Resuming...", variant: "outline", className: "border-blue-500 text-blue-600 animate-pulse" },
  archived: { label: "Archived", variant: "outline", className: "border-muted-foreground/50 text-muted-foreground bg-muted/50" },
  success: { label: "Success", variant: "default", className: "bg-success hover:bg-success/90" },
  disconnected: { label: "Disconnected", variant: "destructive", className: "bg-red-500/10 text-red-600 border-red-500/20" },
  syncing: { label: "Syncing", variant: "secondary", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  reconnect_needed: { label: "Reconnect Needed", variant: "secondary", className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  error: { label: "Error", variant: "destructive" },
  draft: { label: "Draft", variant: "outline" },
  churned: { label: "Churned", variant: "secondary", className: "bg-slate-500/10 text-slate-600 border-slate-500/20" },
};

export const StatusBadge = ({ status, className }: StatusBadgeProps) => {
  const config = statusConfig[status] || { label: status, variant: "outline" as const };
  
  return (
    <Badge 
      variant={config.variant}
      className={cn(config.className, className)}
    >
      {config.label}
    </Badge>
  );
};
